<template>
  <div :class="wrapperClasses">
    <i v-if="icon"
       :class="iconClassFix"
    ></i>
    <input
      class="form-control mdb-autocomplete bridge8-autocomplete"
      type="text"
      :size="size"
      :disabled="disabled"
      v-model="display"
      @input="onChange"
      @keyup.down="onArrowDown"
      @keyup.up="onArrowUp"
      @keyup.enter="onEnter"
      @focus="focus"
      @blur="blur"
      ref="autocompleteRef"
      :placeholder="placeholder"
    />
    <small class='text-muted' v-if="small">{{ small }}</small>
    <ul class="mdb-autocomplete-wrap text-left"
        v-show="isOpen"
    >
      <li
        class="loading"
        v-if="isLoading">
        {{asyncLoadingText}}
      </li>
      <li
        v-if="nullValueString !== null"
        @click="setResult(null)"
        :class="{'is-active': arrowCounter === -1}"
      >{{ nullValueString }}
      </li>
      <li v-if="nullValueString !== null" class="p-0"><hr class="m-0" /></li>
      <li
        v-for="(result, i) in results"
        :key="i"
        @click="setResult(result)"
        :class="{'is-active': i === arrowCounter}"
      >
        <mdb-icon v-if="iconCallback !== null" :icon="iconCallback(result)" class="mr-3" />
        <bridge8-popover
          v-if="isExtended"
          :obj="objectTableDataCallback(result)"
          :header-prefix="`${result.type}: `"
          :text-callback="objectDataTextCallback"
        />
        <div v-else>{{ isObjectData ? objectDataTextCallback(result) : result }}</div>
      </li>
    </ul>
    <button class="clear" :style="{visibility: isClearVisible}"
            @click="handleClear"
    >
      <svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xml="https://www.w3.org/2000/svg">
        <path
          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        <path d="M0 0h24v24H0z" fill="none"/>
      </svg>
    </button>
    <label v-if="label"
           class="bridge8-autocomplete"
           :class="{'active': isLabelActive, 'disabled': disabled, 'required': required}"
           @click="handleLabelClick"
    >{{label}}</label>
  </div>
</template>
<script>
import { classNames } from '../utils'
import { mdbIcon } from 'mdbvue'
import Bridge8Popover from './Bridge8Popover'

const Bridge8Autocomplete = {
  name: 'Bridge8Autocomplete',
  props: {
    size: {
      type: String,
      default: 'lg'
    },
    small: {
      type: String,
      default: null
    },
    data: {
      type: Array,
      required: false,
      default: () => []
    },
    nullValueString: {
      type: String,
      required: false,
      default: null
    },
    isObjectData: {
      type: Boolean,
      required: false,
      default: false
    },
    isExtended: {
      type: Boolean,
      required: false,
      default: false
    },
    objectDataTextCallback: {
      type: Function,
      required: false,
      default: obj => obj ? obj.name : null
    },
    objectTableDataCallback: {
      type: Function,
      required: false,
      default: obj => obj
    },
    iconCallback: {
      type: Function,
      required: false,
      default: null
    },
    minCharsToSearch: {
      type: Number,
      required: false,
      default: 1
    },
    filterRequired: {
      type: Boolean,
      required: false,
      default: true
    },
    isAsync: {
      type: Boolean,
      required: false,
      default: false
    },
    asyncLoadingText: {
      type: String,
      required: false,
      default: 'Loading results...'
    },
    label: {
      type: String,
      required: false
    },
    required: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false
    },
    icon: {
      type: String,
      required: false
    },
    iconClass: {
      type: [String, Array],
      required: false
    },
    far: {
      type: Boolean,
      default: false
    },
    regular: {
      type: Boolean,
      default: false
    },
    fal: {
      type: Boolean,
      default: false
    },
    light: {
      type: Boolean,
      default: false
    },
    fab: {
      type: Boolean,
      default: false
    },
    brands: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    searchString: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      search: this.searchString,
      results: [],
      isOpen: false,
      isLoading: false,
      isTouched: false,
      arrowCounter: this.nullValueString === null ? -1 : -2
    }
  },
  mounted () {
    document.addEventListener('click', this.handleClickOutside)
  },
  destroyed () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    // searching functionality:
    onChange () {
      this.$emit('input', this.search)
      if (this.isAsync && this.search.length >= this.minCharsToSearch) {
        this.isLoading = true
      }
      if (this.search.length < this.minCharsToSearch) {
        this.isOpen = false
      } else {
        this.isOpen = true
        if (this.filterRequired) {
          this.filterResults()
        }
      }
    },
    filterResults () {
      this.results = this.data.filter(item => {
        const stringToCheck = (this.isObjectData ? this.objectDataTextCallback(item) : item) || ''
        return stringToCheck.toLowerCase().indexOf((this.search || '').toLowerCase()) > -1
      })
    },
    setResult (result) {
      this.search = this.isObjectData ? this.objectDataTextCallback(result) : result
      this.isOpen = false
      this.$emit('change', result)
    },
    // arrow keys functionality:
    onArrowDown () {
      if (this.arrowCounter < this.results.length) {
        this.arrowCounter++
      }
    },
    onArrowUp () {
      if (this.arrowCounter > 0) {
        this.arrowCounter--
      }
    },
    onEnter () {
      const result = this.results[this.arrowCounter]
      this.search = this.isObjectData ? this.objectDataTextCallback(result) : result
      this.isOpen = false
      this.arrowCounter = -1
      this.$emit('change', result)
    },
    // outside click functionality:
    handleClickOutside (evt) {
      if (!this.$el.contains(evt.target)) {
        this.isOpen = false
        this.arrowCounter = -1
      }
    },
    // clear button functionality:
    handleClear () {
      this.search = ''
      this.isOpen = false
      this.arrowCounter = -1
      this.$emit('input', this.search)
      this.$emit('change', null)
    },

    // handle click:
    handleLabelClick () {
      this.$refs.autocompleteRef.focus()
      this.isTouched = true
    },
    focus (e) {
      if (this.minCharsToSearch === 0) {
        this.filterResults()
        this.isOpen = true
      }
      this.$emit('focus', e)
      this.isTouched = true
    },
    blur (e) {
      this.$emit('blur', e)
      this.isTouched = false
    }
  },
  watch: {
    data: {
      handler (value, oldValue) {
        // we want to make sure we only do this when it's an async request
        if (this.isAsync) {
          this.results = value
          this.isOpen = true
          this.isLoading = false
        }
      }
    },
    searchString (newVal, oldVal) {
      this.search = newVal
    }
  },
  computed: {
    display: {
      get () {
        return ['', null].includes(this.search) ? (this.isTouched ? null : this.nullValueString) : this.search
      },
      set (value) {
        this.search = value
      }
    },
    // label dynamic behavior:
    wrapperClasses () {
      return classNames(
        'md-form',
        this.outline && 'md-outline'
      )
    },
    isLabelActive () {
      return !!(
        this.isTouched === true ||
        this.placeholder ||
        this.display !== this.nullValueString ||
        (this.display === this.nullValueString && !['', null].includes(this.nullValueString))
      )
    },
    isClearVisible () {
      if (this.search && !this.disabled) {
        return 'visible'
      }
      return 'hidden'
    },
    iconClassFix () {
      return classNames(
        this.far || this.regular ? 'far'
          : this.fal || this.light ? 'fal'
            : this.fab || this.brands ? 'fab' : 'fas',
        'prefix fa-' + this.icon,
        this.isTouched && 'active',
        this.iconClass
      )
    }
  },
  components: {
    mdbIcon,
    Bridge8Popover
  }
}

export default Bridge8Autocomplete
</script>

<style lang="sass" scoped>
  input.bridge8-autocomplete:disabled
    background-color: lightgray!important

  label.bridge8-autocomplete
    &.active
      width: auto
      padding-left: 0.5em!important
      padding-right: 0.5em!important
      margin-left: 0.5em!important
      border: 1px solid transparent
      border-radius: 1em
    &.disabled
      background-color: white!important
      border-color: white!important
    &.required
      &:after
        content: " *"

</style>

<style>
  .is-active {
    background-color: #eeeeee;
  }

  .clear {
    position: absolute;
    z-index: 2;
    top: .5rem;
    right: 0;
    border: none;
    background: 0 0;
    padding-bottom: 0.2rem
  }

  .clear svg {
    fill: #a6a6a6;
  }
</style>



// WEBPACK FOOTER //
// src/components/Bridge8Autocomplete.vue